import { useState, useEffect, useCallback } from "react";
import { Cloud, Trash2, Loader2, Plus, ExternalLink, Eye, EyeOff, LogIn } from "lucide-react";
import { api, type CloudCredential, type CloudDeployment } from "@/lib/api";

import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";

interface CloudSectionProps {
  projectId: string;
}

const PROVIDER_LABELS: Record<string, string> = {
  digitalocean: "DigitalOcean",
  vercel: "Vercel",
  railway: "Railway",
};

const PROVIDERS = [
  { id: "digitalocean", label: "DigitalOcean", hint: "API token from api.digitalocean.com/account/api/tokens" },
] as const;

type ProviderKey = (typeof PROVIDERS)[number]["id"];

export function CloudSection({ projectId }: CloudSectionProps) {
  // projectId is still used for listing deployments (per-project),
  // but credentials are now user-level (shared across projects)
  const [credentials, setCredentials] = useState<CloudCredential[]>([]);
  const [deployments, setDeployments] = useState<CloudDeployment[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [useManualToken, setUseManualToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Add form state
  const [provider, setProvider] = useState<ProviderKey>("digitalocean");
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [creds, deps] = await Promise.all([
        api.cloud.listCredentials(),
        api.cloud.listDeployments(projectId),
      ]);
      setCredentials(creds);
      setDeployments(deps);
    } catch {
      // Silently fail — section just shows empty
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Listen for OAuth popup completing (cross-tab localStorage signal)
  useEffect(() => {
    function handleStorage(e: StorageEvent) {
      if (e.key === "digitalocean-connected") {
        loadData();
      }
    }
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [loadData]);

  function handleOAuthConnect() {
    const url = api.cloud.digitalOceanOAuthUrl();
    const popup = window.open(url, "digitalocean-oauth", "popup");
    if (!popup || popup.closed) {
      window.location.href = url;
    }
  }

  async function handleSave() {
    if (!token.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.cloud.createCredential({ provider, token: token.trim() });
      setToken("");
      setAdding(false);
      setUseManualToken(false);
      setShowToken(false);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    }
    setSaving(false);
  }

  async function handleDelete(credId: string) {
    setDeleting(credId);
    try {
      await api.cloud.deleteCredential(credId);
      await loadData();
    } catch (err) {
      console.error("[CloudSection] delete failed:", err);
    }
    setDeleting(null);
  }

  // Which providers already have credentials
  const connectedProviders = new Set(credentials.map((c: CloudCredential) => c.provider));
  const availableProviders = PROVIDERS.filter((p) => !connectedProviders.has(p.id));

  if (loading) {
    return (
      <section>
        <h3 className="text-2xs uppercase tracking-widest text-daydra-muted mb-2.5 px-1">
          Cloud
        </h3>
        <div className="flex items-center justify-center py-4">
          <Loader2 size={16} className="animate-spin text-daydra-muted" />
        </div>
      </section>
    );
  }

  return (
    <section>
      <h3 className="text-2xs uppercase tracking-widest text-daydra-muted mb-2.5 px-1">
        Cloud
      </h3>

      <div className="rounded-xl p-3.5 bg-white/[0.04] border border-white/[0.06] space-y-3">
        {/* Connected providers */}
        {credentials.map((cred) => (
          <div key={cred.id} className="flex items-center gap-2.5">
            <Cloud size={15} className="text-daydra-dim shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-daydra-text truncate">
                {PROVIDER_LABELS[cred.provider] || cred.provider}
              </p>
              {cred.label && (
                <p className="text-2xs text-daydra-muted truncate">
                  {cred.label}
                </p>
              )}
            </div>
            <IconButton
              variant="danger"
              size="sm"
              onClick={() => handleDelete(cred.id)}
              disabled={deleting === cred.id}
              aria-label={`Remove ${cred.provider} key`}
            >
              {deleting === cred.id ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Trash2 size={13} />
              )}
            </IconButton>
          </div>
        ))}

        {/* Active deployments */}
        {deployments
          .filter((d) => d.status !== "destroyed")
          .map((dep) => (
            <div
              key={dep.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/[0.03]"
            >
              <div
                className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                  dep.status === "active"
                    ? "bg-daydra-success"
                    : dep.status === "error"
                      ? "bg-daydra-error"
                      : "bg-daydra-warning animate-pulse"
                }`}
              />
              <span className="text-xs text-daydra-dim truncate flex-1">
                {dep.app_name}
              </span>
              {dep.url && (
                <a
                  href={dep.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-daydra-muted hover:text-daydra-accent transition-colors"
                >
                  <ExternalLink size={12} />
                </a>
              )}
            </div>
          ))}

        {/* Add provider form */}
        {adding ? (
          <div className="space-y-2.5 pt-1">
            {availableProviders.length > 1 && (
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value as ProviderKey)}
                className="w-full h-9 rounded-xl bg-daydra-raised px-3 text-xs text-daydra-text border border-daydra-border focus:outline-none focus:border-daydra-accent/50"
              >
                {availableProviders.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            )}

            {/* OAuth connect (primary) */}
            {provider === "digitalocean" && !useManualToken && (
              <>
                <Button
                  variant="default"
                  size="sm"
                  className="w-full flex items-center justify-center gap-1.5 text-xs"
                  onClick={handleOAuthConnect}
                >
                  <LogIn size={13} className="shrink-0" />
                  <span className="truncate">Connect with DigitalOcean</span>
                </Button>

                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-white/[0.06]" />
                  <span className="text-2xs text-daydra-muted">or</span>
                  <div className="flex-1 h-px bg-white/[0.06]" />
                </div>

                <button
                  onClick={() => setUseManualToken(true)}
                  className="text-2xs text-daydra-muted hover:text-daydra-dim transition-colors w-full text-center"
                >
                  Paste an API token instead
                </button>

                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setAdding(false);
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
              </>
            )}

            {/* Manual token input (fallback or non-DO providers) */}
            {(provider !== "digitalocean" || useManualToken) && (
              <>
                <div className="relative">
                  <Input
                    type={showToken ? "text" : "password"}
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="Paste API token"
                    className="h-9 text-xs pr-9"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSave();
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-daydra-muted hover:text-daydra-dim transition-colors"
                  >
                    {showToken ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>

                {error && (
                  <p className="text-xs text-daydra-error">{error}</p>
                )}

                <p className="text-2xs text-daydra-muted leading-relaxed">
                  {PROVIDERS.find((p) => p.id === provider)?.hint}
                </p>

                <div className="flex gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    className="flex-1"
                    onClick={handleSave}
                    disabled={saving || !token.trim()}
                  >
                    {saving ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      "Save"
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      setAdding(false);
                      setUseManualToken(false);
                      setToken("");
                      setError(null);
                      setShowToken(false);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          availableProviders.length > 0 && (
            <button
              onClick={() => setAdding(true)}
              className="flex items-center gap-2 w-full text-xs text-daydra-muted hover:text-daydra-accent transition-colors duration-fast"
            >
              <Plus size={13} />
              <span>
                {credentials.length === 0
                  ? "Connect cloud provider"
                  : "Add another provider"}
              </span>
            </button>
          )
        )}

        {/* Empty state */}
        {credentials.length === 0 && !adding && (
          <p className="text-2xs text-daydra-muted text-center leading-relaxed">
            Add your cloud API key to deploy through chat.
            You pay your provider directly.
          </p>
        )}
      </div>
    </section>
  );
}
